// frontend/src/pages/AdminUsers.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import { AuthContext } from "../contexts/AuthContext";
import { getUserAccess } from "../utils/userPermissions";

const USERS_ADMIN_LIST_URL = "/users/admin/users/";
const buildRoleUrl = (userId) => `/users/admin/users/${userId}/role/`;
const buildActivationUrl = (userId) => `/users/admin/users/${userId}/activation/`;

// Ajusta estos values si tu backend usa strings exactos distintos
const ROLE_OPTIONS = [
  { value: "usuario", label: "Usuario" },
  { value: "admin", label: "Admin" },
  { value: "superusuario", label: "Superusuario" },
  { value: "superusuarioplus", label: "Superusuario Plus" },
  { value: "superusuarioplusultra", label: "Superusuario Plus Ultra" },
];

function getRowsFromPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getCountFromPayload(payload, fallbackLength) {
  if (typeof payload?.count === "number") return payload.count;
  return fallbackLength;
}

function buildDisplayName(row) {
  const fullName = `${row?.first_name ?? ""} ${row?.last_name ?? ""}`.trim();
  return fullName || row?.full_name || row?.username || row?.email || `Usuario #${row?.id}`;
}

function getEmployeeNumber(row) {
  return (
    row?.employee_number ||
    row?.employee_code ||
    row?.numero_empleado ||
    row?.employee ||
    "—"
  );
}

function readApiError(err) {
  const data = err?.response?.data;

  if (!data) return err?.message || "Error desconocido";

  if (typeof data === "string") return data;
  if (data.detail) return data.detail;

  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return "Error desconocido";
  }
}

export default function AdminUsers() {
  const { user } = useContext(AuthContext);
  const access = getUserAccess(user);

  const [users, setUsers] = useState([]);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyRoleUserId, setBusyRoleUserId] = useState(null);
  const [busyActivationUserId, setBusyActivationUserId] = useState(null);
  const [error, setError] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  // ✅ SOLO Superusuarioplusultra
  const canManageUsers = access.canAccessSuperPlusUltra;

  const filteredRoleOptions = useMemo(() => ROLE_OPTIONS, []);

  const fetchUsers = async () => {
    if (!canManageUsers) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      const q = String(searchTerm || "").trim();
      if (q) params.set("search", q);

      const url = `${USERS_ADMIN_LIST_URL}${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await apiClient.get(url);

      const rows = getRowsFromPayload(response.data);
      setUsers(rows);
      setTotalCount(getCountFromPayload(response.data, rows.length));

      const nextDrafts = {};
      rows.forEach((row) => {
        nextDrafts[row.id] = row?.role || "";
      });
      setRoleDrafts(nextDrafts);
    } catch (err) {
      console.error(err);
      setUsers([]);
      setTotalCount(0);
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageUsers]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    await fetchUsers();
  };

  const handleClearSearch = async () => {
    setSearchTerm("");
    try {
      setLoading(true);
      setError("");

      const response = await apiClient.get(USERS_ADMIN_LIST_URL);
      const rows = getRowsFromPayload(response.data);

      setUsers(rows);
      setTotalCount(getCountFromPayload(response.data, rows.length));

      const nextDrafts = {};
      rows.forEach((row) => {
        nextDrafts[row.id] = row?.role || "";
      });
      setRoleDrafts(nextDrafts);
    } catch (err) {
      console.error(err);
      setUsers([]);
      setTotalCount(0);
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleDraftChange = (userId, nextRole) => {
    setRoleDrafts((prev) => ({
      ...prev,
      [userId]: nextRole,
    }));
  };

  const handleSaveRole = async (targetUser) => {
    const nextRole = roleDrafts[targetUser.id];

    if (!nextRole) {
      alert("Selecciona un rol válido.");
      return;
    }

    if (String(nextRole) === String(targetUser?.role || "")) {
      alert("No hubo cambio de rol.");
      return;
    }

    const ok = window.confirm(
      `¿Cambiar el rol de "${buildDisplayName(targetUser)}" de "${targetUser?.role || "—"}" a "${nextRole}"?`
    );
    if (!ok) return;

    try {
      setBusyRoleUserId(targetUser.id);

      await apiClient.patch(buildRoleUrl(targetUser.id), {
        role: nextRole,
      });

      await fetchUsers();

      alert(
        "Rol actualizado correctamente.\n\nAhora ve a Django Admin → Bitácoras de auditoría y refresca para validar el registro."
      );
    } catch (err) {
      console.error(err);
      alert(`No se pudo cambiar el rol.\n\n${readApiError(err)}`);
    } finally {
      setBusyRoleUserId(null);
    }
  };

  const handleToggleActivation = async (targetUser) => {
    const nextValue = !Boolean(targetUser?.is_active);

    const ok = window.confirm(
      `${nextValue ? "¿Activar" : "¿Desactivar"} a "${buildDisplayName(targetUser)}"?`
    );
    if (!ok) return;

    try {
      setBusyActivationUserId(targetUser.id);

      await apiClient.patch(buildActivationUrl(targetUser.id), {
        is_active: nextValue,
      });

      await fetchUsers();

      alert(
        `Usuario ${nextValue ? "activado" : "desactivado"} correctamente.\n\nAhora ve a Django Admin → Bitácoras de auditoría y refresca para validar el registro.`
      );
    } catch (err) {
      console.error(err);
      alert(`No se pudo actualizar la activación.\n\n${readApiError(err)}`);
    } finally {
      setBusyActivationUserId(null);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="border border-red-200 bg-red-50 text-red-800 rounded p-4">
          No tienes permisos para administrar usuarios. Esta pantalla es exclusiva para Superusuario Plus Ultra.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Administración de usuarios</h1>
        <p className="text-sm text-gray-600 mt-1">
          Usa esta pantalla para generar cambios reales de rol y activación, y luego validarlos en Django Admin → Bitácoras de auditoría.
        </p>
      </div>

      <div className="mb-6 border border-blue-200 bg-blue-50 text-blue-900 rounded p-4">
        <div className="font-semibold mb-2">Flujo recomendado de prueba</div>
        <ol className="list-decimal ml-5 text-sm space-y-1">
          <li>Abre Django Admin → Bitácoras de auditoría en otra pestaña.</li>
          <li>Desde aquí cambia el rol o activa/desactiva un usuario.</li>
          <li>Regresa al Django Admin y refresca.</li>
          <li>Verifica que apareció el registro nuevo con actor, target_user, action, old_value y new_value.</li>
        </ol>
      </div>

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          className="w-full md:w-[420px] px-3 py-2 border rounded"
          placeholder="Buscar por nombre, correo o número de empleado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <button
          type="submit"
          className="px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
          disabled={loading}
        >
          Buscar
        </button>

        <button
          type="button"
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          onClick={handleClearSearch}
          disabled={loading}
        >
          Limpiar
        </button>

        <button
          type="button"
          className="px-4 py-2 rounded bg-slate-200 hover:bg-slate-300"
          onClick={fetchUsers}
          disabled={loading}
        >
          Refrescar
        </button>

        <div className="ml-auto text-sm text-gray-600">
          {loading ? "Cargando..." : `Usuarios: ${totalCount}`}
        </div>
      </form>

      {error && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-800 rounded p-3 text-sm whitespace-pre-wrap">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border rounded bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left border-b">ID</th>
              <th className="px-3 py-2 text-left border-b">Nombre</th>
              <th className="px-3 py-2 text-left border-b">Correo</th>
              <th className="px-3 py-2 text-left border-b">No. empleado</th>
              <th className="px-3 py-2 text-left border-b">Rol actual</th>
              <th className="px-3 py-2 text-center border-b">Activo</th>
              <th className="px-3 py-2 text-left border-b">Cambiar rol</th>
              <th className="px-3 py-2 text-center border-b">Acción</th>
            </tr>
          </thead>

          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  {loading ? "Cargando..." : "No hay usuarios para mostrar."}
                </td>
              </tr>
            ) : (
              users.map((row) => {
                const isBusyRole = busyRoleUserId === row.id;
                const isBusyActivation = busyActivationUserId === row.id;

                return (
                  <tr key={row.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2">{row.id}</td>
                    <td className="px-3 py-2">{buildDisplayName(row)}</td>
                    <td className="px-3 py-2">{row?.email || "—"}</td>
                    <td className="px-3 py-2">{getEmployeeNumber(row)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {row?.role || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                          row?.is_active
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {row?.is_active ? "Sí" : "No"}
                      </span>
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <select
                          className="border rounded px-2 py-1 min-w-[220px]"
                          value={roleDrafts[row.id] ?? row?.role ?? ""}
                          onChange={(e) => handleRoleDraftChange(row.id, e.target.value)}
                          disabled={isBusyRole || isBusyActivation}
                        >
                          <option value="">— Selecciona rol —</option>
                          {filteredRoleOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          className={`px-3 py-1 rounded text-white ${
                            isBusyRole ? "bg-blue-300" : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          onClick={() => handleSaveRole(row)}
                          disabled={isBusyRole || isBusyActivation}
                        >
                          {isBusyRole ? "Guardando..." : "Guardar rol"}
                        </button>
                      </div>
                    </td>

                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        className={`px-3 py-1 rounded text-white ${
                          isBusyActivation
                            ? "bg-amber-300"
                            : row?.is_active
                              ? "bg-red-600 hover:bg-red-700"
                              : "bg-green-600 hover:bg-green-700"
                        }`}
                        onClick={() => handleToggleActivation(row)}
                        disabled={isBusyRole || isBusyActivation}
                      >
                        {isBusyActivation
                          ? "Guardando..."
                          : row?.is_active
                            ? "Desactivar"
                            : "Activar"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}